import { type Translated } from '@myre/common-i18n';
import { type DisplayMode } from '@myre/ui-primitive-core';

export enum MenuBlockType {
  Secondary = 'secondary',
  Tertiary = 'tertiary',
}

export interface MenuBlock {
  type: MenuBlockType;
  items: ConsolidatedMenuItem[];
}

/**
 * A menu item is a recursive structure.
 * It can have children, which are also menu items.
 */
export interface MenuItem {
  label: Translated;
  icon?: string;
  secondaryIcon?: string;
  hidden?: boolean | (() => boolean);
  children?: MenuItem[];
}

// We need to compute the absolutePath property, based on the parent's absolutePath
export interface ConsolidatedMenuItem extends MenuItem {
  path: string;
  absolutePath: string;
  alternateAbsolutePaths?: string[];
  expanded: boolean;
  children?: ConsolidatedMenuItem[];
  secondaryIcon?: string;
}

export interface ForcedDisplayMode {
  open: boolean;
  displayMode: DisplayMode;
}

/**
 * A group of the menu. Its key identifies the group, its label is what the reader sees.
 * `W` is the wordings tree of the app declaring the route.
 */
export interface MenuNode<W = never> {
  key: string;
  label: (wordings: W) => Translated;
}

export interface MenuItemData<W = never> {
  label: (wordings: W) => Translated;
  node?: MenuNode<W>;
  icon?: string;
  secondaryIcon?: string;
  /**
   * Sibling routes this entry also owns, as sibling paths. The entry stays
   * selected on them although it links elsewhere — what a module being rebuilt
   * behind a sister route needs, so the user is not left with no selection.
   */
  alternatePaths?: string[];
}

/** A menu entry once its labels are read from the wordings of the app. */
export interface ResolvedMenuItem {
  label: Translated;
  node?: { key: string; label: Translated };
  icon?: string;
  secondaryIcon?: string;
  alternatePaths?: string[];
}
